<script setup lang="ts">
	import { useForm } from 'ant-design-vue/es/form'
	import useAccountApi from '~/services/apis/account'

	const { UpdateAccountPassword } = useAccountApi()
	const passwordUpdateForm = reactive({
		password: '',
		new_password: '',
		confirm_password: ''
	})
	const { accountInfo } = useAuth()
	const { resetFields } = useForm(passwordUpdateForm)
	const isUpdatePasswordFormEmpty = computed(() => {
		return Object.values(passwordUpdateForm).every(item => !!item)
	})
	const validatePass = (_: unknown, e: string) => {
		return new Promise((resolve, reject) => {
			if (!e) {
				return reject(new Error('Password is required'))
			}
			if (e !== passwordUpdateForm.new_password) {
				return reject(new Error('Passwords do not match'))
			}
			resolve(true)
		})
	}
	const processingUpdate = ref(false)
	const submitPasswords = async () => {
		const { data } = await resolve(
			() =>
				UpdateAccountPassword({
					...passwordUpdateForm,
					business_id: accountInfo.value?.id || ''
				}),
			processingUpdate
		)

		if (data.value) {
			resolve(() => useMixpanel()?.track('change_password_biz_successful'))
			useNotification().alert({
				status: data.value.responseCode,
				message: data.value.responseText
			})
			resetFields()
		} else {
			resolve(() => useMixpanel()?.track('change_password_biz_failed'))
		}
	}
</script>

<template>
	<shared-card wrapper-class="max-w-full" default-class="sm:p-12 p-8 inline-flex max-w-[900px] w-full">
		<div class="mx-auto w-full max-w-[570px] space-y-8">
			<header>
				<h1 class="text-lg font-medium text-gray-700">Security</h1>
				<p class="text-gray-400">Change your password</p>
			</header>
			<a-form
				:model="passwordUpdateForm"
				:validate-messages="antValidateMessages"
				autocomplete="off"
				class="w-full"
				@finish="submitPasswords"
			>
				<a-form-item label="Current Password" name="password" required class="!mb-4">
					<a-input-password
						v-model:value="passwordUpdateForm.password"
						autocomplete="new password"
						placeholder="Enter your current password"
						class="shadow-0 h-[44px] rounded-[8px] bg-gray-50 px-6 outline-none hover:border-primary-2 focus:border-primary-2 focus:shadow-none"
					/>
				</a-form-item>

				<a-form-item label="New Password" name="new_password" required class="!mb-4">
					<a-input-password
						v-model:value="passwordUpdateForm.new_password"
						autocomplete="new password"
						placeholder="Enter your new password"
						class="shadow-0 h-[44px] rounded-[8px] bg-gray-50 px-6 outline-none hover:border-primary-2 focus:border-primary-2 focus:shadow-none"
					/>
				</a-form-item>

				<a-form-item
					label="Confirm New Password"
					name="confirm_password"
					:rules="{
						validator: validatePass
					}"
					class="!mb-4"
				>
					<a-input-password
						v-model:value="passwordUpdateForm.confirm_password"
						autocomplete="new password"
						placeholder="Confirm your new password"
						class="shadow-0 h-[44px] rounded-[8px] bg-gray-50 px-6 outline-none hover:border-primary-2 focus:border-primary-2 focus:shadow-none"
					/>
				</a-form-item>

				<a-button
					class="default-btn mt-10 w-full justify-center bg-primary-2 text-white"
					html-type="submit"
					:loading="processingUpdate"
					:disabled="!isUpdatePasswordFormEmpty || processingUpdate"
				>
					{{ processingUpdate ? 'updating...' : 'Update Password' }}
				</a-button>
			</a-form>
		</div>
	</shared-card>
</template>
